<template>
    <div v-if="landingPageData.template_id == 1" class="container pt-12">
        <AppLandingHero :is-landing-hero="true" class="mb-12 lg:mb-[120px]" :hero="heroSectionData" />
        <AppLandingSection2
            class="mb-12 lg:mb-[120px]"
            :title="sectionTwoTitle"
            :subtitle="sectionTwoSubtitle"
            :images="landingImages.slice(0, 6)"
            :base-index="1"
        />
        <AppLandingSection3 :item="thirdSection" class="mb-12 lg:mb-[120px]" />
        <!-- <AppAboutService
            :landing-page-data="howToSectionData"
            :used-in-ideas="true"
            class="bg-[#2b2b2b] text-gray_100 mb-12 lg:mb-[120px]"
        /> -->
        <AppLandingSection2
            class="mb-12 lg:mb-[120px]"
            :title="sectionTwoTitle"
            :subtitle="sectionTwoSubtitle"
            :images="landingImages?.slice(6)"
            :base-index="7"
        />
        <AppLandingFurniture
            :title="furnitureTitle"
            class="mb-12 lg:mb-[120px]"
            :furniture-images="furnitureImagesData"
        />

        <div class="grid lg:grid-cols-3 sm:grid-cols-2 gap-x-6 gap-y-6 lg:gap-y-12 lg:mb-[126px] mb-14">
            <NuxtLinkLocale
                v-for="card in thumbnails"
                :to="`/ideas/${card.key}`"
                class="p-4 bg-gray-850 rounded-2xl flex"
            >
                <img class="rounded-xl w-24" :src="card.image" :alt="card.title" />
                <div class="ml-2">
                    <div v-html="card.title" class="text-body-lg font-bold text-gray-100 mb-1 lg:w-52"></div>
                    <p class="text-gray-100 text-body-sm leading-4">
                        {{ card.description }}
                    </p>
                </div>
            </NuxtLinkLocale>
        </div>
    </div>
    <div v-else>
        <AppLandingSecondTHero :hero="heroSectionData" />
        <div class="py-20 text-center w-full container">
            <h2 class="lg:text-title-lg text-title-md font-bold tracking-tighter text-gray-100 mb-2">
                {{ heroSectionData?.h2 }}
            </h2>
            <p class="text-gray-200 text-body-md leading-5">{{ heroSectionData?.sub_h2 }}</p>
        </div>
        <AppLandingMasonryStyle :items="landingImages" />

        <AppCompareTabGroups :content="designHubTools" />
        <AppIdeationCenter :content="IDContent" id="homepage-ideationcenter-button" />

        <!-- <AppLandingExplore /> -->
        <div
            class="relative background py-20 px-4 lg:px-20 overflow-hidden flex flex-col lg:flex-row justify-between rounded-3xl text-center lg:text-start mx-4 lg:container"
        >
            <img class="absolute -top-8 rotate-180 lg:left-0" src="/images/stars-bg.png" />
            <div class="max-w-[500px] relative z-20">
                <h2 class="mb-2 text-title-lg lg:text-display-md font-bold text-gray-100">
                    Find Suitable Furniture Set For Your Home!
                </h2>
                <p class="text-body-md lg:text-body-lg text-gray-300 leading-6 mb-6 lg:mb-0">
                    Explore various styles & colors, find the ideal match for your space, shop it instantly form top
                    local & global retailers!
                </p>
            </div>
            <NuxtLink to="/dashboard/services" class="h-fit my-auto max-w-[200px] mx-auto lg:me-0">
                <AppButton variant="outlined" color="brand" block> Get Started </AppButton>
            </NuxtLink>
        </div>
        <div class="container">
            <div
                class="py-10 lg:px-10 px-4 bg-gray-900 lg:my-20 my-10 rounded-2xl mx-4 lg:mx-auto max-w-[400px] lg:max-w-full"
            >
                <h2
                    class="lg:text-title-lg text-title-md font-bold text-gray-100 lg:text-center text-start lg:mb-6 mb-2"
                >
                    {{ aboutService.title }}
                </h2>

                <div class="text-body-md lg:text-body-lg text-gray-400 leading-6">
                    <span>{{ aboutService.description }}</span>

                    <TransitionExpand>
                        <span v-if="showContent">{{ aboutService.fullDescription }}</span>
                    </TransitionExpand>
                </div>
                <div>
                    <AppButton @click="showContent = !showContent" variant="ghost">
                        {{ showContent ? "Read less" : "Read More " }}
                    </AppButton>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import AppLandingHero from "../../../components/app-landing/AppLandingHero.vue";
import { getIdeasItems } from "~/api/global/ideas";
import { useHomepageSection } from "~/data/homepage";

const { designHubTools } = useHomepageSection();

import type {
    IHeroSection,
    IHowToSection,
    ILandingImages,
    ILandingPageData,
    ISeo,
    IThumbnails,
} from "~/types/pages/landing-page.ts";

const landingTitle = useRoute().params.landing;

const metaTitle = ref("");
const metaDescription = ref("");
const heroSectionData = ref<IHeroSection>();
const sectionTwoTitle = ref("");
const sectionTwoSubtitle = ref("");
const thumbnails = ref<IThumbnails[]>([]);
const showContent = ref(false);

const IDContent = <IIdeationCenter>{
    title: "More to Explore",
    items: [
        {
            text: "Modern Master Bedroom Furniture",
            link: "/ideas/modern-master-bedroom-furniture",
            image: {
                src: "https://s3.amazonaws.com/ideas-after/984c6348-65c5-423c-9539-7b4f082763c5.jpeg",
                alt: "modern master bedroom",
            },
        },
        {
            text: "Scandinavian White Kitchen",
            link: "/ideas/scandinavian-white-kitchen",
            image: {
                src: "https://s3.amazonaws.com:443/ideas-after/85217df3-9ee8-4f1c-9042-5b8e9073d93d.jpeg",
                alt: "scandinavian kitchen",
            },
        },
        {
            text: "White Farmhouse Bedroom",
            link: "/ideas/white-farmhouse-bedroom",
            image: {
                src: "https://s3.amazonaws.com:443/ideas-after/8bc02097-a0c2-49fd-81b6-6f3af18486ee.jpeg",
                alt: "white farmhouse",
            },
        },
        {
            text: "Blue and Orange Living Room",
            link: "/ideas/blue-and-orange-living-room",
            image: {
                src: "https://s3.amazonaws.com:443/ideas-after/65c4112b-f6aa-4d7e-8220-5f28ae1474b3.jpeg",
                alt: "living room",
            },
        },
    ],
    cta: {
        text: "See More Ideas",
        link: "/ideas",
    },
};

const thirdSection = {
    subtitle:
        "AI HomeDesign is the one-stop shop for top-notch, AI-powered, and effortless real estate photo editing services.",

    btn: {
        text: "Generate Ideas Now",
        Link: "/",
    },
};

const howToSectionData = ref<IHowToSection>({
    title: "",
    completeText: "",
    summaryText: "",
});

const landingImages = ref<ILandingImages[]>();

const furnitureTitle = ref("");
const furnitureImagesData = ref<ILandingImages[]>();

const { data, error, pending } = await getIdeasItems(`landing/${landingTitle}`);

const landingPageData: ILandingPageData = data.value as ILandingPageData;

thumbnails.value = [...landingPageData.thumbnails];

const seo: ISeo = landingPageData.seo;
heroSectionData.value = landingPageData.hero;

metaTitle.value = seo.title;
metaDescription.value = seo.description;

sectionTwoTitle.value = landingPageData.hero.h2 as string;
sectionTwoSubtitle.value = landingPageData.hero.sub_h2 as string;

const { images }: { images: ILandingImages } = landingPageData;
const createArrayOfImages = (objOfImages: ILandingImages, numOfItems: number) => {
    let tempArrayOfImages: ILandingImages[] = [];
    for (let index = 0; index < numOfItems; index += 4) {
        const sliced = Object.keys(objOfImages)
            .slice(index, index + 4)
            .reduce((result, key) => {
                //@ts-ignore
                result[key] = objOfImages[key];

                // obj["key3"] = value3;
                return result;
            }, {});

        tempArrayOfImages.push(sliced);
    }

    return tempArrayOfImages;
};

landingImages.value = createArrayOfImages(images, Object.keys(landingPageData.images).length);
for (let index = 0; index < landingImages.value.length; index++) {
    const element = landingImages.value[index];
}

const furnitureData = landingPageData.furniture;
furnitureTitle.value = furnitureData.furniture_title;
howToSectionData.value["title"] = furnitureData.how_to_section_title;
howToSectionData.value.summaryText = furnitureData.how_to_section_content;
howToSectionData.value.completeText = furnitureData.how_to_section_content?.replace(/(?:\r\n|\r|\n)/g, "<br>");

let furnitureItemsIncludeImages: ILandingImages = {};
for (const key in furnitureData) {
    if (key.split("_").includes("image")) {
        furnitureItemsIncludeImages[key] = furnitureData[key];
    }
}

furnitureImagesData.value = createArrayOfImages(furnitureItemsIncludeImages, 24);

useHead({
    title: metaTitle.value,
    meta: [
        {
            name: "description",
            content: metaDescription.value,
        },
    ],
    link: [
        {
            rel: "canonical",
            href: `${useRoute().path}`,
        },
    ],
});
const aboutService = {
    title: landingPageData?.furniture?.how_to_section_title,
    description: landingPageData.furniture.how_to_section_content
        ? useDevice().isMobile
            ? landingPageData?.furniture?.how_to_section_content.substring(0, 500)
            : landingPageData?.furniture?.how_to_section_content.substring(0, 2000)
        : "",
    fullDescription: landingPageData.furniture.how_to_section_content
        ? useDevice().isMobile
            ? landingPageData?.furniture?.how_to_section_content.substring(500)
            : landingPageData?.furniture?.how_to_section_content.substring(2000)
        : "",
};
</script>
<style>
.background {
    background: linear-gradient(0deg, #000 0.1%, #4d4dbd 100.1%);
}
@media only screen and (min-width: 768px) {
    .background {
        background: linear-gradient(270deg, #000 0%, #4d4dbd 123.54%);
    }
}
</style>
